import {
    createHealthStat,
    getPopularityRelatedEntityGrowthHealthStat,
} from '../../state/utils';
import {
    getRelatedBrandsPopularityData,
    getRelatedCategoriesInPairsPopularityData,
    getRelatedCategoriesPopularityData,
} from '../api/popularity.api';
import { toNumberIfNumeric } from 'utils/helpers';
import { useInfiniteQuery } from 'react-query';

const pluralDimensionToSingle = { categories: 'category', brands: 'brand' };
const apiFn = (domain) =>
    domain === 'categories'
        ? getRelatedCategoriesPopularityData
        : getRelatedBrandsPopularityData;

const queryFn = async ({ queryKey, pageParam = 1 }) => {
    const {
        paginateBy,
        order_by,
        ascending,
        country,
        category,
        brand,
        related_category,
        per_page,
        affinity_group,
        price_segmentation,
        search,
        includeParentId,
        fetch_in_pairs,
    } = queryKey[1];
    let data;
    if (fetch_in_pairs) {
        data = await getRelatedCategoriesInPairsPopularityData({
            data: {
                category,
                brand,
                country,
                order_by,
                ascending,
                per_page,
                page: pageParam,
                affinity_group,
                price_segmentation,
                search,
                related_category,
            },
        });
    } else {
        data = await apiFn(paginateBy)({
            data: {
                category,
                brand,
                country,
                order_by,
                ascending,
                per_page,
                page: pageParam,
                affinity_group,
                price_segmentation,
                search,
            },
        });
    }
    return {
        ...data,
        data: data?.data?.map((record) => {
            const id = record[`${pluralDimensionToSingle[paginateBy]}_id`];
            const {
                data,
                category_name,
                brand_name,
                price,
                parent_trend_name,
            } = record;
            const { google_share_prc, instagram_share_prc, tiktok_share_prc } =
                data;
            const shapedData = {
                ...data,
                parentName: data.affinity_group?.map((s) => s.toLowerCase()),
                week: record.week,
                year: record.year,
                parentTrendName: parent_trend_name,
                google_share_prc: toNumberIfNumeric(google_share_prc),
                instagram_share_prc: toNumberIfNumeric(instagram_share_prc),
                tiktok_share_prc: toNumberIfNumeric(tiktok_share_prc),
            };
            shapedData.healthStats = {
                growth: getPopularityRelatedEntityGrowthHealthStat(data),
            };
            if (paginateBy === 'brands') {
                const pricePointHealthStat = createHealthStat('price point', {
                    price_segmentation: price,
                });
                shapedData.healthStats = {
                    ...shapedData.healthStats,
                    'price point': pricePointHealthStat,
                };
                shapedData.price_segmentation = pricePointHealthStat?.score;
            }
            const itemName =
                paginateBy === 'categories' ? category_name : brand_name;
            return {
                ...shapedData,
                searchField: itemName,
                name: itemName,
                id,
                dataPointId: record.id,
                ...(includeParentId
                    ? { parentId: record.category || record.brand }
                    : {}),
            };
        }),
    };
};

export const getQueryKeyRelatedEntityData = ({
    order_by,
    ascending,
    categories,
    brands,
    related_categories,
    countries,
    affinity_group,
    price_segmentation,
    search,
    per_page,
    paginateBy,
    includeParentId,
    fetch_in_pairs,
}) => {
    return [
        'PopIndexRelatedEntityData',
        {
            order_by,
            ascending,
            category: categories,
            brand: brands,
            related_category: related_categories,
            country: countries,
            affinity_group,
            price_segmentation,
            search,
            per_page,
            paginateBy,
            includeParentId: !!includeParentId,
            fetch_in_pairs: !!fetch_in_pairs,
        },
    ];
};

const usePopularityRelatedEntities = ({
    per_page = 500,
    order_by = 'popularity_index_avg_12_month',
    ascending,
    paginateBy,
    brands,
    categories,
    related_categories,
    countries,
    affinity_group,
    price_segmentation,
    search,
    enabled = true,
    placeholderData,
    includeParentId = false,
    keepPreviousData = true,
    fetch_in_pairs = false,
}) => {
    const queryKey = getQueryKeyRelatedEntityData({
        order_by,
        ascending,
        categories,
        brands,
        related_categories,
        countries,
        affinity_group,
        price_segmentation,
        search,
        per_page,
        paginateBy,
        includeParentId,
        fetch_in_pairs,
    });
    const query = useInfiniteQuery(queryKey, queryFn, {
        enabled,
        staleTime: Infinity,
        getNextPageParam: (lastPage) => lastPage?.next_page ?? undefined,
        keepPreviousData,
        placeholderData: placeholderData?.length
            ? () => ({
                  pages: [
                      {
                          data: placeholderData,
                          total_records: placeholderData?.length || 0,
                          last_page: 1,
                          messages: null,
                          next_page: null,
                          page: 1,
                          per_page,
                          prev_page: null,
                      },
                  ],
              })
            : undefined,
    });
    const getQueryData = async () => {
        const currentPages = query?.data?.pages || [];
        const proms = [];
        for (
            let i = currentPages.length + 1;
            i <= query?.data?.pages[0]?.last_page;
            i++
        ) {
            proms.push(queryFn({ queryKey, pageParam: i }));
        }
        const restPages = await Promise.all(proms);
        return [...currentPages, ...restPages].map(({ data }) => data).flat();
    };
    return {
        query,
        queryKey,
        getQueryData,
    };
};

export default usePopularityRelatedEntities;
